/*!
 * SPDX-License-Identifier: AGPL-3.0-only
 * 
 * PV Tool — AGPL Community Edition
 * Based on the last AGPL-3.0 version published on 2026-03-18
 * 
 * Copyright (c) 2026 DanteAlighieri13210914
 * Copyright (c) 2026 Contributors to PV Tool AGPL Community Edition
 * 
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, version 3 of the License.
 * 
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 * 
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.
 * 
 * Source repository: https://github.com/yandujun363/pv-tool-agpl
 */

import type { TemplateConfig } from '../core/types';

export const kawaiPixelTemplate: TemplateConfig = {
  name: 'Kawaii像素',
  palette: {
    background: '#fef0f5',
    primary: '#ffb3d9',
    secondary: '#b3e5fc',
    accent: '#c8f7dc',
    text: '#5a3a5a',
  },
  bpm: 120,
  animationSpeed: 1.0,
  bgOpacity: 1.0,
  effects: [
    // 背景 - 柔和的像素点阵
    {
      type: 'dotScreen',
      layer: 'background',
      config: {
        color: '#ffb3d9',
        dotSize: 4,
        spacing: 12,
        alpha: 0.15,
      },
    },
    // 像素网格背景（淡淡的）
    {
      type: 'checkerboard',
      layer: 'background',
      config: {
        color1: '#ffffff',
        color2: '#fef5f8',
        cellSize: 40,
        alpha: 0.3,
      },
    },
    // 左侧桌面图标 - Paint.exe
    {
      type: 'desktopIcon',
      layer: 'decoration',
      config: {
        x: 30,
        y: 30,
        size: 64,
        iconType: 'paint',
        label: 'Paint.exe',
        labelColor: '#5a3a5a',
      },
    },
    // 左侧桌面图标 - Notes
    {
      type: 'desktopIcon',
      layer: 'decoration',
      config: {
        x: 30,
        y: 120,
        size: 64,
        iconType: 'notes',
        label: 'Notes',
        labelColor: '#5a3a5a',
      },
    },
    // 左上角窗口 - Paint.exe风格
    {
      type: 'pixelWindow',
      layer: 'decoration',
      config: {
        x: 0.22,
        y: 0.18,
        anchorX: 0.5,
        anchorY: 0.5,
        width: 320,
        height: 260,
        bgColor: '#ffffff',
        borderColor: '#ffb3d9',
        borderWidth: 4,
        titleBgColor: '#ffb3d9',
        titleColor: '#ffffff',
        title: 'Pixel Paint',
        titleBarHeight: 28,
        icon: 'heart',
        iconColor: '#ffb3d9',
        iconSize: 70,
        alpha: 0.95,
      },
    },
    // 右上角窗口 - Welcome风格
    {
      type: 'pixelWindow',
      layer: 'decoration',
      config: {
        x: 0.75,
        y: 0.15,
        anchorX: 0.5,
        anchorY: 0.5,
        width: 280,
        height: 180,
        bgColor: '#f0f8ff',
        borderColor: '#b3e5fc',
        borderWidth: 4,
        titleBgColor: '#b3e5fc',
        titleColor: '#ffffff',
        title: 'Welcome!!',
        titleBarHeight: 28,
        icon: 'paint',
        iconColor: '#b3e5fc',
        iconSize: 50,
        alpha: 0.92,
      },
    },
    // Messages窗口 - 叠放在Welcome窗口右下侧
    {
      type: 'pixelWindow',
      layer: 'decoration',
      config: {
        x: 0.82,
        y: 0.28,
        anchorX: 0.5,
        anchorY: 0.5,
        width: 260,
        height: 160,
        bgColor: '#fff5f8',
        borderColor: '#ffc0e0',
        borderWidth: 4,
        titleBgColor: '#ffc0e0',
        titleColor: '#ffffff',
        title: 'Messages ♡',
        titleBarHeight: 28,
        content: 'You have 3 new\nmessages! (◕‿◕)',
        contentColor: '#5a3a5a',
        alpha: 0.90,
      },
    },
    // 左下角窗口 - Music Player风格
    {
      type: 'pixelWindow',
      layer: 'decoration',
      config: {
        x: 0.18,
        y: 0.78,
        anchorX: 0.5,
        anchorY: 0.5,
        width: 300,
        height: 200,
        bgColor: '#f5f0ff',
        borderColor: '#c8b3ff',
        borderWidth: 4,
        titleBgColor: '#c8b3ff',
        titleColor: '#ffffff',
        title: 'Music Player',
        titleBarHeight: 28,
        content: '♪ Now Playing...\n\n▶ Track 01\n━━━━━━━━━━ 2:34',
        contentColor: '#5a3a5a',
        alpha: 0.93,
      },
    },
    // Calendar窗口 - 叠放在Music Player窗口上方
    {
      type: 'pixelWindow',
      layer: 'decoration',
      config: {
        x: 0.25,
        y: 0.68,
        anchorX: 0.5,
        anchorY: 0.5,
        width: 240,
        height: 150,
        bgColor: '#f0fff4',
        borderColor: '#c8f7dc',
        borderWidth: 4,
        titleBgColor: '#c8f7dc',
        titleColor: '#ffffff',
        title: 'Calendar',
        titleBarHeight: 28,
        content: '📅 Today:\nMarch 14, 2026\nSaturday ☆',
        contentColor: '#5a3a5a',
        alpha: 0.91,
      },
    },
    // 右下角窗口 - Note.txt风格
    {
      type: 'pixelWindow',
      layer: 'decoration',
      config: {
        x: 0.78,
        y: 0.78,
        anchorX: 0.5,
        anchorY: 0.5,
        width: 340,
        height: 240,
        bgColor: '#fffef0',
        borderColor: '#ffb3d9',
        borderWidth: 4,
        titleBgColor: '#ffb3d9',
        titleColor: '#ffffff',
        title: 'Note.txt',
        titleBarHeight: 28,
        content: '1. Buy milk\n2. Call mom\n3. Practice drawing\n4. Be cute!',
        contentColor: '#5a3a5a',
        alpha: 0.95,
      },
    },
    // 装饰 - 可爱的心形像素（散落在画面中）
    {
      type: 'scatteredShapes',
      layer: 'decoration',
      config: {
        shape: 'circle',
        color: '#ffb3d9',
        count: 6,
        minSize: 12,
        maxSize: 20,
        alpha: 0.5,
        speed: 0.2,
      },
    },
    // 装饰 - 星星像素
    {
      type: 'scatteredShapes',
      layer: 'decoration',
      config: {
        shape: 'circle',
        color: '#fff9b3',
        count: 8,
        minSize: 8,
        maxSize: 16,
        alpha: 0.6,
        speed: 0.15,
      },
    },
    // 中央像素字体文字 - 打字机效果（左右分布）
    {
      type: 'pixelTypewriter',
      layer: 'text',
      config: {
        fillColor: '#5a3a5a',
        strokeColor: '#ffffff',
        fontSize: 44,
        strokeWidth: 6,
        fontWeight: '900',
        fontFamily: '"Courier New", monospace',
        letterSpacing: 3,
        leftX: 0.2,
        rightX: 0.6,
        y: 0.5,
        maxCharsPerSide: 5,
        shadowColor: '#ffb3d9',
        shadowBlur: 0,
        shadowOffsetX: 3,
        shadowOffsetY: 3,
        charDelay: 0.08,
        cursorColor: '#ffb3d9',
        cursorWidth: 4,
        cursorBlinkSpeed: 0.5,
        showCursorWhenDone: false,
        pixelSize: 3,
      },
    },
  ],
};
